import { useEffect, useState } from 'react';
import { Box, Flex, Group, Loader, Stack, Text } from '@mantine/core';
import { UserRound } from 'lucide-react';
import { useParams } from 'react-router-dom';
import type { ReactElement } from 'react';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SessionDriftUserViewModel } from './SessionDriftUserViewModel';
import type { AnalyticsSurface } from './AnalyticsSurface';
import { formatCount, formatTokens, humanUser } from '@/shared/analytics/MetricFormatting';
import SurfaceBreadcrumbRow from './SurfaceBreadcrumbRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from './PageLevelFilterRow';
import type { StatItem } from '@/design/StatBand';
import DriftSessionTopicGrid from './DriftSessionTopicGrid';
import { onTopicEfficiency } from './DriftMetrics';
import TopicSummaryPanel from './TopicSummaryPanel';
import SessionDriftEfficiencyCard from './SessionDriftEfficiencyCard';
import InteractionRecordDrawer from './InteractionRecordDrawer';
import TopicNotFound from './TopicNotFound';
import { SurfaceAssistToolbar } from './assist/SurfaceAssistScope';
import { useAssistFilters } from './assist/AssistSurfaceProvider';

/**
 * The person drill-in for Session Drift: one person's activity grouped by
 * session then topic, with each session's drift tokens (turns off its dominant
 * topic) rolled up. Replaces the old per-session detail — the L1 now drills to a
 * person, so the primary route key is a `user_id`.
 */
export default function SessionDriftUserDetailBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const { topicId, primaryKey } = useParams<{ topicId: string; primaryKey: string }>();
    const vm = useViewModel(SessionDriftUserViewModel);
    const filters = useAssistFilters();
    const [selected, setSelected] = useState<InteractionRecord | null>(null);
    const userId = primaryKey === undefined ? undefined : decodeURIComponent(primaryKey);

    useEffect(() => {
        void vm.load(surface.id, topicId, userId, filters);
    }, [vm, surface.id, topicId, userId, filters]);

    if (vm.notFound) return <TopicNotFound />;

    const definition = vm.definition;
    if (definition === null || vm.userId === null) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Gathering sessions…
                </Text>
            </Group>
        );
    }

    const displayLabel = humanUser(vm.userId);
    const summary = vm.summary;
    const stats: StatItem[] = [
        { label: 'Prompts', value: formatCount(summary.prompts) },
        { label: 'Sessions', value: formatCount(summary.sessions) },
        { label: 'Topics', value: formatCount(summary.topics) },
        { label: 'Total tokens', value: formatTokens(summary.totalTokens) },
        { label: 'Drift tokens', value: formatTokens(summary.driftTokens) },
    ];
    const efficiency = onTopicEfficiency(summary.totalTokens, summary.driftTokens);

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow
                items={[
                    ...(surface.drillStyle === 'side-rail' ? [] : [{ label: surface.title, to: surface.basePath }]),
                    { label: definition.navLabel ?? definition.title, to: `${surface.basePath}/${definition.id}` },
                    { label: displayLabel },
                ]}
            />

            <PageHeaderComponent
                icon={UserRound}
                eyebrow="Person · sessions"
                title={displayLabel}
                subtitle={`Every session ${displayLabel} ran, grouped by the topics it drifted across — with the context tokens each drift burned.`}
            />

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && vm.turns.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                </Group>
            :   <Flex gap="lg" align="flex-start" direction={{ base: 'column', lg: 'row' }}>
                    <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
                        {vm.turns.length === 0 ?
                            <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: '14px 16px', background: 'var(--bm-color-surface)' }}>
                                <Text size="sm" c="dimmed">
                                    No sessions found for this person in this window.
                                </Text>
                            </Box>
                        :   <DriftSessionTopicGrid turns={vm.turns} onSelect={setSelected} persistenceKey={`drift-detail:${surface.id}:${definition.id}`} />
                        }
                    </Box>
                    <Box style={{ width: 300, flexShrink: 0, maxWidth: '100%' }}>
                        <Stack gap="md">
                            <TopicSummaryPanel stats={stats} />
                            <SessionDriftEfficiencyCard
                                efficiency={efficiency}
                                driftTokens={summary.driftTokens}
                                totalTokens={summary.totalTokens}
                                totalCostUsd={summary.totalCostUsd}
                                driftCostUsd={summary.driftCostUsd}
                            />
                        </Stack>
                    </Box>
                </Flex>
            }

            <InteractionRecordDrawer record={selected} onClose={() => setSelected(null)} accent={definition.accent} />
        </Box>
    );
}
